import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce } from 'react-use';

import { get } from 'lodash';
import moment from 'moment';
import { axiosService } from 'ui-model';

import isOnEmbedPage from '@lib/utils/isOnEmbedPage';

import { signOut } from '../actions';
import { selectCurrentToken } from '../selectors';
import storage from '../services/storageService';

import { IFrameId, MessageFormat } from './constants';

const getCurrentPathArray = (): Array<string> => {
  const currentPath = window.location.pathname;
  if (currentPath.includes('settings/features/') || currentPath.includes('settings/utilities/')) {
    const currentPathString = currentPath.replace('/embed/', '');
    return [currentPathString];
  }

  if (currentPath.includes('patients/')) {
    const currentPathString = currentPath.includes('edit')
      ? currentPath.slice(0, currentPath.lastIndexOf('/')).replace('/embed/', '')
      : currentPath.replace('/embed/', '');
    return [currentPathString];
  }

  const currentPathArray = currentPath.split('/');
  return currentPathArray;
};

const getIFrameID = () => {
  const currentPathArray = getCurrentPathArray();
  const iFrameIdKey = currentPathArray.filter((el) => IFrameId[el as keyof typeof IFrameId]);
  const iFrameId: IFrameId = IFrameId[iFrameIdKey[iFrameIdKey.length - 1] as keyof typeof IFrameId];
  return iFrameId || IFrameId.ACTIVITY_FEED;
};

// in case broadcast channel is not support uses local storage
const supportsBroadcastAPI = (() => window && (window as any).BroadcastChannel)();
const channel = supportsBroadcastAPI ? new BroadcastChannel('session-data') : null;

const sendMessageToUpperScreen = () => {
  const topWindow = window.parent;

  if (isOnEmbedPage()) {
    const message: MessageFormat = {
      version: 'v1',
      messageType: 'heartbeat',
      screenId: getIFrameID(),
    };
    topWindow.postMessage(JSON.stringify(message), '*');
  }
};

function useCheckSessionExpired(minutes: number, pathOnExpire: string | null, enabled: boolean = true) {
  const dispatch = useDispatch();
  const miliseconds = minutes * 60 * 1000;
  const [sessionExpired, setSessionExpired] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const shouldPostIframeMessageRef = useRef<boolean>(true);

  const currentToken =
    useSelector(selectCurrentToken) || get(axiosService.defaults.headers.common, "['x-access-token']", '');

  useEffectOnce(() => {
    setInterval(() => {
      if (shouldPostIframeMessageRef.current) {
        sendMessageToUpperScreen();
        shouldPostIframeMessageRef.current = false;
      }
    }, 60000);
  });

  const initializeTimer = useCallback(
    (event?: KeyboardEvent | MouseEvent) => {
      shouldPostIframeMessageRef.current = true;
      // Clear previous timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Start a new timer and show session expired if timeout
      timeoutRef.current = setTimeout(() => {
        setSessionExpired(true);
      }, miliseconds);

      // TODO: Fix a memory leak issue related to this code ASAP
      // check if is not propagating the event from other window/tab and
      // send a message to other window/tabs to renew user session and prevent them to expire
      if (event) {
        if (channel) {
          channel.postMessage({ renewSession: true });
        } else {
          // preserve data, eg telehealth information
          storage.set('session-data', { ...storage.get('session-data'), renewSession: true });
        }
      }
    },
    [miliseconds],
  );

  const addListeners = useCallback(() => {
    // Restart timer each time there's a click or keydown
    window.addEventListener('click', initializeTimer);
    window.addEventListener('keydown', initializeTimer);
  }, [initializeTimer]);

  const removeListeners = useCallback(() => {
    window.removeEventListener('click', initializeTimer);
    window.removeEventListener('keydown', initializeTimer);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [initializeTimer, timeoutRef]);

  useEffect(() => {
    if (!enabled || !currentToken) {
      removeListeners();
      return;
    }
    if (currentToken) {
      if (moment().isAfter(currentToken.exp)) {
        setSessionExpired(true);
      } else if (!timeoutRef.current) {
        addListeners();
        initializeTimer();
      }
    }
  }, [enabled, currentToken, addListeners, removeListeners, initializeTimer]);

  useEffect(() => {
    if (sessionExpired && pathOnExpire) {
      removeListeners();
      dispatch(signOut({ path: pathOnExpire }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionExpired, removeListeners, pathOnExpire]);

  useEffect(() => {
    const updateFromBroadcastChannel = (event: MessageEvent) => {
      if (get(event.currentTarget, 'name', '') === 'session-data') {
        initializeTimer();
        // start/stop user interaction listeners to prevent session expiration
        if (get(event.data, 'disableSessionExpirationCheck', false)) {
          removeListeners();
        } else {
          addListeners();
        }
      }
    };

    const updateFromLocalStorage = (event: any) => {
      if (event.storageArea !== localStorage && event.key === 'session-data') {
        initializeTimer();
        // start/stop user interaction listeners to prevent session expiration
        if (get(event, 'newValue.disableSessionExpirationCheck', false)) {
          removeListeners();
        } else {
          addListeners();
        }
      }
    };

    // listeners to validate user session through multiple window/tabs
    if (channel) {
      channel.onmessage = updateFromBroadcastChannel;
    } else {
      window.addEventListener('storage', updateFromLocalStorage);
    }

    return () => {
      if (!channel) {
        window.removeEventListener('storage', updateFromLocalStorage);
      }
    };
  }, [addListeners, initializeTimer, removeListeners]);

  return sessionExpired;
}

export default useCheckSessionExpired;
